import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Check, ShieldCheck, CreditCard, User, Package, ChevronRight, ArrowLeft, AlertCircle } from 'lucide-react';
import Button from '../components/Button'; // Ensure this points to src/components/Button.tsx
import { useAuth } from '../context/AuthContext';
import API_BASE_URL from '../config';
import './Register.css';

interface Plan {
    id: string;
    name: string;
    price: number;
    description: string;
    features: string[];
    color: string;
}

const PLANS: Plan[] = [
    {
        id: 'basic',
        name: 'Basic',
        price: 300,
        description: 'Perfect for intimate weddings',
        features: ['100 Guest Invitations', 'Standard Support', 'RSVP Management'],
        color: '#f8fafc'
    },
    {
        id: 'silver',
        name: 'Silver',
        price: 999,
        description: 'Most popular for grand weddings',
        features: ['250 Guest Invitations', 'Priority Support', 'Custom Wedding Domain', 'AI RSVP Agent'],
        color: '#f1f5f9'
    },
    {
        id: 'gold',
        name: 'Gold',
        price: 1999,
        description: 'Unlimited premium experience',
        features: ['Unlimited Guests', 'Dedicated Manager', 'WhatsApp Broadcast', 'Custom Branding'],
        color: '#fffbeb'
    }
];

const Register: React.FC = () => {
    const history = useHistory();
    const location = useLocation();
    const { setUser, user } = useAuth();
    
    // Parse query params
    const queryParams = new URLSearchParams(location.search);
    const initialPlan = queryParams.get('plan');

    const [step, setStep] = useState(() => {
        // If already logged in, go straight to payment/confirmation page
        const savedUser = localStorage.getItem('user');
        if (savedUser || user) {
            return 3;
        }
        return 1;
    });
    
    // Sync step and selected plan dynamically when url or user auth changes
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const planParam = queryParams.get('plan');
        const stepParam = queryParams.get('step');

        if (planParam && PLANS.some(p => p.id === planParam)) {
            setFormData(prev => ({ ...prev, selectedPlan: planParam }));
            if (user || localStorage.getItem('user')) {
                setStep(3);
            }
        } else if (stepParam === '3') {
            setStep(3);
        }
    }, [location.search, user]);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        password: '',
        coupleName: '',
        groomName: '',
        brideName: '',
        weddingDate: '',
        city: '',
        selectedPlan: initialPlan && PLANS.some(p => p.id === initialPlan) ? initialPlan : 'silver',
        paymentMethod: 'upi'
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        // Scroll to top on step change
        window.scrollTo(0, 0);
        setError(null);
    }, [step]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setError(null);
    };

    const handlePlanSelect = (planId: string) => {
        setFormData({ ...formData, selectedPlan: planId });
        setStep(3);
    };

    const handlePhase1Register = async () => {
        if (!formData.name || !formData.email || !formData.password || !formData.phone || 
            !formData.coupleName || !formData.groomName || !formData.brideName || !formData.weddingDate || !formData.city) {
            setError('Please fill in all fields (including wedding details)');
            return;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setError('Please enter a valid email address');
            return;
        }

        // Phone validation (Indian 10-digit)
        const phoneRegex = /^[6-9]\d{9}$/;
        if (!phoneRegex.test(formData.phone)) {
            setError('Please enter a valid 10-digit phone number');
            return;
        }

        // Password strength validation
        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
        if (!passwordRegex.test(formData.password)) {
            setError('Password must be at least 8 characters and include uppercase, lowercase, number, and special character (@$!%*?&)');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${API_BASE_URL}/api/admin/register`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: formData.email,
                    password: formData.password,
                    name: formData.name,
                    phone: formData.phone,
                    selectedPlan: formData.selectedPlan,
                    coupleName: formData.coupleName,
                    groomName: formData.groomName,
                    brideName: formData.brideName,
                    weddingDate: formData.weddingDate,
                    city: formData.city
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Registration failed');
            }

            // Set user in context and localStorage
            const userData = {
                id: data.admin.id,
                email: data.admin.email,
                name: data.admin.name,
                role: 'admin' as const,
                status: data.admin.status,
                weddingPlan: data.admin.selectedPlan,
            };

            setUser(userData);
            localStorage.setItem('user', JSON.stringify(userData));
            localStorage.setItem('accessToken', data.accessToken);
            localStorage.setItem('refreshToken', data.refreshToken);

            setStep(initialPlan ? 3 : 2); // If they already chose a plan, go to payment, else go to plan selection
        } catch (err: any) {
            setError(err.message || 'Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handlePhase2Payment = async () => {
        const token = localStorage.getItem('accessToken');
        if (!token) {
            setError('Session expired. Please login again.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${API_BASE_URL}/api/admin/confirm-payment`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    planId: formData.selectedPlan,
                    paymentId: 'PAY-' + Math.random().toString(36).substr(2, 9).toUpperCase()
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Payment confirmation failed');
            }

            // Update user in context
            if (user) {
                const updatedUser = {
                    ...user,
                    status: 'active' as const,
                    weddingPlan: data.admin.selectedPlan
                };
                setUser(updatedUser);
                localStorage.setItem('user', JSON.stringify(updatedUser));
            }

            setStep(4); // Success step
        } catch (err: any) {
            setError(err.message || 'Payment failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const renderStep1 = () => (
        <div className="register-step fade-in">
            <div className="step-header">
                <div className="step-icon-wrapper">
                    <User className="step-icon" size={24} />
                </div>
                <h2>Create Your Account</h2>
                <p>Register and set up your wedding workspace in one go</p>
            </div>
            <div className="register-form">
                <div className="form-section-title">👤 Your Information</div>
                <div className="form-grid-2">
                    <div className="form-group">
                        <label>Full Name</label>
                        <input type="text" name="name" placeholder="Enter your name" value={formData.name} onChange={handleInputChange} required />
                    </div>
                    <div className="form-group">
                        <label>Phone Number</label>
                        <input type="tel" name="phone" placeholder="+91 00000 00000" value={formData.phone} onChange={handleInputChange} required />
                    </div>
                </div>
                
                <div className="form-group">
                    <label>Email Address</label>
                    <input type="email" name="email" placeholder="you@example.com" value={formData.email} onChange={handleInputChange} required />
                </div>
                <div className="form-group">
                    <label>Password</label>
                    <input type="password" name="password" placeholder="••••••••" value={formData.password} onChange={handleInputChange} required />
                </div>

                <div className="form-section-title">💍 Wedding Details</div>
                <div className="form-group">
                    <label>Couple Name (e.g. Rahul & Priya)</label>
                    <input type="text" name="coupleName" placeholder="Enter couple name" value={formData.coupleName} onChange={handleInputChange} required />
                </div>

                <div className="form-grid-2">
                    <div className="form-group">
                        <label>Groom's Name</label>
                        <input type="text" name="groomName" placeholder="Groom's name" value={formData.groomName} onChange={handleInputChange} required />
                    </div>
                    <div className="form-group">
                        <label>Bride's Name</label>
                        <input type="text" name="brideName" placeholder="Bride's name" value={formData.brideName} onChange={handleInputChange} required />
                    </div>
                </div>

                <div className="form-grid-2">
                    <div className="form-group">
                        <label>Wedding Date</label>
                        <input 
                            type="date" 
                            name="weddingDate" 
                            value={formData.weddingDate} 
                            onChange={handleInputChange} 
                            required 
                            min={new Date().toISOString().split('T')[0]}
                        />
                    </div>
                    <div className="form-group">
                        <label>City</label>
                        <input type="text" name="city" placeholder="e.g. Indore" value={formData.city} onChange={handleInputChange} required />
                    </div>
                </div>

                {error && (
                    <div className="error-banner fade-in">
                        <AlertCircle size={18} />
                        <span>{error}</span>
                    </div>
                )}
                <Button 
                    label={loading ? 'Creating Account...' : 'Register Account'} 
                    onClick={handlePhase1Register} 
                    className="register-btn"
                    disabled={loading}
                    icon={<ChevronRight size={18} />}
                />
            </div>
        </div>
    );

    const renderStep2 = () => (
        <div className="register-step fade-in">
            <div className="step-header">
                <div className="step-icon-wrapper">
                    <Package className="step-icon" size={24} />
                </div>
                <h2>Select Your Plan</h2>
                <p>Choose the perfect package for your celebration</p>
            </div>
            <div className="plans-selection-grid">
                {PLANS.map(plan => (
                    <div 
                        key={plan.id} 
                        className={`plan-selection-card ${formData.selectedPlan === plan.id ? 'active' : ''}`}
                        onClick={() => handlePlanSelect(plan.id)}
                    >
                        {plan.id === 'silver' && <div className="popular-badge">Recommended</div>}
                        <div className="plan-selection-header">
                            <h3>{plan.name}</h3>
                            <div className="plan-selection-price">
                                <span className="currency">₹</span>
                                <span className="amount">{plan.price}</span>
                                <span className="period">/mo</span>
                            </div>
                        </div>
                        <p className="plan-selection-desc">{plan.description}</p>
                        <ul className="plan-selection-features">
                            {plan.features.map((f, i) => (
                                <li key={i}><Check size={14} /> {f}</li>
                            ))}
                        </ul>
                        <button className="select-plan-btn">Select {plan.name}</button>
                    </div>
                ))}
            </div>
            <div className="back-btn-wrapper">
                <button className="back-btn" onClick={() => setStep(1)}>
                    <ArrowLeft size={16} /> Back to account details
                </button>
            </div>
        </div>
    );

    const renderStep3 = () => {
        const selectedPlan = PLANS.find(p => p.id === formData.selectedPlan);
        return (
            <div className="register-step fade-in">
                <div className="step-header">
                    <div className="step-icon-wrapper">
                        <CreditCard className="step-icon" size={24} />
                    </div>
                    <h2>Secure Payment</h2>
                    <p>Complete your registration to unlock all features</p>
                </div>
                
                <div className="payment-layout">
                    <div className="order-summary">
                        <h3>Order Summary</h3>
                        <div className="summary-row">
                            <span>{selectedPlan?.name} Plan</span>
                            <span>₹{selectedPlan?.price}</span>
                        </div>
                        <div className="summary-row">
                            <span>Platform Fee</span>
                            <span>₹0</span>
                        </div>
                        <div className="summary-divider"></div>
                        <div className="summary-total">
                            <span>Total Amount</span>
                            <span>₹{selectedPlan?.price}</span>
                        </div>
                        <div className="secure-badge">
                            <ShieldCheck size={16} /> 100% Secure Checkout
                        </div>
                    </div>

                    <div className="payment-methods">
                        <h3>Payment Method</h3>
                        <div className="methods-grid">
                            <div 
                                className={`method-card ${formData.paymentMethod === 'upi' ? 'active' : ''}`}
                                onClick={() => setFormData({...formData, paymentMethod: 'upi'})}
                            >
                                <div className="method-dot"></div>
                                <span>UPI (GPay, PhonePe, Paytm)</span>
                            </div>
                            <div 
                                className={`method-card ${formData.paymentMethod === 'card' ? 'active' : ''}`}
                                onClick={() => setFormData({...formData, paymentMethod: 'card'})}
                            >
                                <div className="method-dot"></div>
                                <span>Credit / Debit Card</span>
                            </div>
                            <div 
                                className={`method-card ${formData.paymentMethod === 'netbanking' ? 'active' : ''}`}
                                onClick={() => setFormData({...formData, paymentMethod: 'netbanking'})}
                            >
                                <div className="method-dot"></div>
                                <span>Net Banking</span>
                            </div>
                        </div>

                        {error && (
                            <div className="error-banner fade-in">
                                <AlertCircle size={18} />
                                <span>{error}</span>
                            </div>
                        )}

                        <div className="payment-action">
                            <Button 
                                label={loading ? 'Processing...' : `Confirm & Pay ₹${selectedPlan?.price}`}
                                onClick={handlePhase2Payment}
                                disabled={loading}
                                className="pay-button"
                            />
                            <p className="payment-note">By clicking pay, you agree to our terms of service.</p>
                        </div>
                    </div>
                </div>
                <div className="back-btn-wrapper">
                    <button className="back-btn" onClick={() => setStep(2)}>
                        <ArrowLeft size={16} /> Change Plan
                    </button>
                </div>
            </div>
        );
    };

    const renderSuccess = () => (
        <div className="register-step success-step fade-in">
            <div className="success-animation">
                <div className="check-mark">
                    <Check size={48} color="white" />
                </div>
            </div>
            <h2>Congratulations!</h2>
            <p>Your wedding workspace is ready. Let's start building your dream celebration together.</p>
            <div className="success-details">
                <div className="detail-item">
                    <span className="label">Account</span>
                    <span className="value">{formData.email}</span>
                </div>
                <div className="detail-item">
                    <span className="label">Plan</span>
                    <span className="value">{PLANS.find(p => p.id === formData.selectedPlan)?.name}</span>
                </div>
            </div>
            <Button 
                label="Go to My Dashboard" 
                onClick={() => history.push('/welcome')} 
                className="success-btn"
            />
        </div>
    );

    return (
        <div className="register-page">
            <div className="register-container">
                <div className="register-progress">
                    <div className={`progress-node ${step >= 1 ? 'active' : ''}`}>
                        <div className="node-circle">1</div>
                        <span>Account</span>
                    </div>
                    <div className="progress-line"></div>
                    <div className={`progress-node ${step >= 2 ? 'active' : ''}`}>
                        <div className="node-circle">2</div>
                        <span>Plans</span>
                    </div>
                    <div className="progress-line"></div>
                    <div className={`progress-node ${step >= 3 ? 'active' : ''}`}>
                        <div className="node-circle">3</div>
                        <span>Payment</span>
                    </div>
                </div>

                <div className="register-card">
                    {step === 1 && renderStep1()}
                    {step === 2 && renderStep2()}
                    {step === 3 && renderStep3()}
                    {step === 4 && renderSuccess()}
                </div>

                <div className="register-help">
                    <p>Need help? Chat with our wedding experts on WhatsApp</p>
                    <a href="https://wa.me/917999782728" target="_blank" rel="noreferrer" className="help-link">
                        WhatsApp Support
                    </a>
                </div>
            </div>
        </div>
    );
};

export default Register;
